import { useState, useEffect } from 'react';
import { Save, RotateCcw, Plus, Trash2, ChevronDown, ChevronUp, Building, Phone, MapPin, Image, Users, Wrench, Shield, FileText, Star, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_OPTIONS = ['Shield', 'Award', 'Clock', 'Users', 'CheckCircle', 'Star', 'Phone', 'MapPin', 'Wrench', 'Home', 'Zap', 'Heart'];

// Collapsible Section Component
const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Card className="mb-4">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            {Icon && <Icon className="w-5 h-5 text-[#BF0D3E]" />}
            {title}
          </span>
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </CardTitle>
      </CardHeader>
      {isOpen && <CardContent>{children}</CardContent>}
    </Card>
  );
};

// Field Component
const Field = ({ label, value, onChange, type = "text", placeholder, multiline = false, rows = 3 }) => (
  <div className="mb-4">
    <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">{label}</label>
    {multiline ? (
      <Textarea 
        value={value || ''} 
        onChange={(e) => onChange(e.target.value)} 
        placeholder={placeholder}
        rows={rows}
        className="w-full"
      />
    ) : (
      <Input 
        type={type}
        value={value || ''} 
        onChange={(e) => onChange(e.target.value)} 
        placeholder={placeholder}
        className="w-full"
      />
    )}
  </div>
);

export default function ContentManagement({ token }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load content');
    }
    setLoading(false);
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content saved successfully!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save content');
    }
    setSaving(false);
  };

  const resetContent = async () => {
    try {
      const response = await axios.post(`${API}/content/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
      setHasChanges(false);
      toast.success('Content reset to defaults');
      setShowResetDialog(false);
    } catch (error) {
      toast.error('Failed to reset content');
    }
  };

  // Why Choose Us card handlers
  const addWhyChooseCard = () => {
    const newCards = [...(content.why_choose_cards || []), { icon: 'Star', title: '', desc: '' }];
    updateField('why_choose_cards', newCards);
  };

  const updateWhyChooseCard = (index, field, value) => {
    const newCards = [...content.why_choose_cards];
    newCards[index] = { ...newCards[index], [field]: value };
    updateField('why_choose_cards', newCards);
  };

  const removeWhyChooseCard = (index) => {
    const newCards = content.why_choose_cards.filter((_, i) => i !== index);
    updateField('why_choose_cards', newCards);
  };

  // Service handlers
  const addServiceCategory = () => {
    const newServices = [...(content.services || []), { name: 'New Category', items: [] }];
    updateField('services', newServices);
  };

  const updateServiceCategory = (catIndex, name) => {
    const newServices = [...content.services];
    newServices[catIndex] = { ...newServices[catIndex], name };
    updateField('services', newServices);
  };

  const removeServiceCategory = (catIndex) => {
    const newServices = content.services.filter((_, i) => i !== catIndex);
    updateField('services', newServices);
  };

  const addServiceItem = (catIndex) => {
    const newServices = [...content.services];
    newServices[catIndex].items = [...newServices[catIndex].items, { title: '', desc: '' }];
    updateField('services', newServices);
  };

  const updateServiceItem = (catIndex, itemIndex, field, value) => {
    const newServices = [...content.services];
    newServices[catIndex].items[itemIndex] = { ...newServices[catIndex].items[itemIndex], [field]: value };
    updateField('services', newServices);
  };

  const removeServiceItem = (catIndex, itemIndex) => {
    const newServices = [...content.services];
    newServices[catIndex].items = newServices[catIndex].items.filter((_, i) => i !== itemIndex);
    updateField('services', newServices);
  };

  // Process steps handlers
  const addProcessStep = () => {
    const stepNum = String((content.process_steps?.length || 0) + 1).padStart(2, '0');
    const newSteps = [...(content.process_steps || []), { step: stepNum, title: '', desc: '' }];
    updateField('process_steps', newSteps);
  };

  const updateProcessStep = (index, field, value) => {
    const newSteps = [...content.process_steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    updateField('process_steps', newSteps);
  };

  const removeProcessStep = (index) => {
    const newSteps = content.process_steps.filter((_, i) => i !== index);
    // Renumber steps
    newSteps.forEach((step, i) => { step.step = String(i + 1).padStart(2, '0'); });
    updateField('process_steps', newSteps);
  };

  // Service areas handlers
  const addServiceArea = () => {
    const newAreas = [...(content.service_areas || []), { city: '', description: '' }];
    updateField('service_areas', newAreas);
  };

  const updateServiceArea = (index, field, value) => {
    const newAreas = [...content.service_areas];
    newAreas[index] = { ...newAreas[index], [field]: value };
    updateField('service_areas', newAreas);
  };

  const removeServiceArea = (index) => {
    const newAreas = content.service_areas.filter((_, i) => i !== index);
    updateField('service_areas', newAreas);
  };

  // Testimonials handlers
  const addTestimonial = () => {
    const newTestimonials = [...(content.testimonials || []), { 
      id: `t${Date.now()}`, 
      name: '', 
      location: '', 
      text: '', 
      rating: 5 
    }];
    updateField('testimonials', newTestimonials);
  };

  const updateTestimonial = (index, field, value) => {
    const newTestimonials = [...content.testimonials];
    newTestimonials[index] = { ...newTestimonials[index], [field]: value };
    updateField('testimonials', newTestimonials);
  };

  const removeTestimonial = (index) => {
    const newTestimonials = content.testimonials.filter((_, i) => i !== index);
    updateField('testimonials', newTestimonials);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Loading content...</div>;
  }

  if (!content) {
    return <div className="flex items-center justify-center h-64 text-gray-500">Failed to load content</div>;
  }

  return (
    <div className="space-y-6" data-testid="content-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#111827] uppercase tracking-tight">Site Content</h1>
          <p className="text-gray-500 mt-1">Manage all website content</p>
        </div>
        
        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600 font-medium">Unsaved changes</span>
          )}
          <Button 
            variant="outline" 
            onClick={() => setShowResetDialog(true)}
            data-testid="reset-content-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button 
            onClick={saveContent} 
            disabled={saving || !hasChanges}
            className="bg-[#BF0D3E] hover:bg-[#8B0029]"
            data-testid="save-content-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Company Info */}
      <Section title="Company Information" icon={Building} defaultOpen={true}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Company Name" value={content.company_name} onChange={(v) => updateField('company_name', v)} />
          <Field label="Tagline" value={content.tagline} onChange={(v) => updateField('tagline', v)} />
          <Field label="Owner 1 Name" value={content.owner1_name} onChange={(v) => updateField('owner1_name', v)} />
          <Field label="Owner 2 Name" value={content.owner2_name} onChange={(v) => updateField('owner2_name', v)} />
        </div>
      </Section>

      {/* Contact Info */}
      <Section title="Contact Information" icon={Phone}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Main Phone" value={content.main_phone} onChange={(v) => updateField('main_phone', v)} />
          <Field label="Emergency Phone (24/7)" value={content.emergency_phone} onChange={(v) => updateField('emergency_phone', v)} />
          <Field label="Email" value={content.email} onChange={(v) => updateField('email', v)} type="email" />
          <Field label="Address" value={content.address} onChange={(v) => updateField('address', v)} />
        </div>
      </Section>

      {/* Hero Section */}
      <Section title="Hero Section" icon={Image}>
        <Field label="Main Headline" value={content.hero_headline} onChange={(v) => updateField('hero_headline', v)} />
        <Field label="Subtext" value={content.hero_subtext} onChange={(v) => updateField('hero_subtext', v)} multiline />
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Trust Badge 1" value={content.hero_badge1} onChange={(v) => updateField('hero_badge1', v)} />
          <Field label="Trust Badge 2" value={content.hero_badge2} onChange={(v) => updateField('hero_badge2', v)} />
          <Field label="Trust Badge 3" value={content.hero_badge3} onChange={(v) => updateField('hero_badge3', v)} />
        </div>
      </Section>

      {/* Why Choose Us */}
      <Section title="Why Choose Us Cards" icon={Shield}>
        <div className="space-y-4">
          {content.why_choose_cards?.map((card, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-gray-700">Card {index + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeWhyChooseCard(index)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">Icon</label>
                  <Select value={card.icon} onValueChange={(v) => updateWhyChooseCard(index, 'icon', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map(icon => (
                        <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input placeholder="Title" value={card.title} onChange={(e) => updateWhyChooseCard(index, 'title', e.target.value)} />
                <Input placeholder="Description" value={card.desc} onChange={(e) => updateWhyChooseCard(index, 'desc', e.target.value)} />
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addWhyChooseCard} className="w-full">
            <Plus className="w-4 h-4 mr-2" /> Add Card
          </Button>
        </div>
      </Section>

      {/* Services */}
      <Section title="Services" icon={Wrench}>
        <div className="space-y-6">
          {content.services?.map((category, catIndex) => (
            <div key={catIndex} className="p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <Input 
                  value={category.name} 
                  onChange={(e) => updateServiceCategory(catIndex, e.target.value)}
                  className="font-semibold text-lg max-w-xs"
                />
                <Button variant="ghost" size="sm" onClick={() => removeServiceCategory(catIndex)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-3 ml-4">
                {category.items?.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-center gap-3 bg-white p-3 rounded border">
                    <div className="flex-1 grid md:grid-cols-2 gap-3">
                      <Input placeholder="Service Title" value={item.title} onChange={(e) => updateServiceItem(catIndex, itemIndex, 'title', e.target.value)} />
                      <Input placeholder="Description" value={item.desc} onChange={(e) => updateServiceItem(catIndex, itemIndex, 'desc', e.target.value)} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeServiceItem(catIndex, itemIndex)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => addServiceItem(catIndex)}>
                  <Plus className="w-4 h-4 mr-2" /> Add Service
                </Button>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addServiceCategory} className="w-full">
            <Plus className="w-4 h-4 mr-2" /> Add Service Category
          </Button>
        </div>
      </Section>

      {/* Insurance Process */}
      <Section title="Insurance Claim Process" icon={FileText}>
        <div className="space-y-4">
          {content.process_steps?.map((step, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-[#BF0D3E]">Step {step.step}</span>
                <Button variant="ghost" size="sm" onClick={() => removeProcessStep(index)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Input placeholder="Step Title" value={step.title} onChange={(e) => updateProcessStep(index, 'title', e.target.value)} />
                <Input placeholder="Description" value={step.desc} onChange={(e) => updateProcessStep(index, 'desc', e.target.value)} />
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addProcessStep} className="w-full">
            <Plus className="w-4 h-4 mr-2" /> Add Step
          </Button>
        </div>
      </Section>

      {/* Service Areas */}
      <Section title="Service Areas" icon={MapPin}>
        <div className="space-y-4">
          {content.service_areas?.map((area, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded border">
              <div className="flex-1 grid md:grid-cols-2 gap-3">
                <Input placeholder="City" value={area.city} onChange={(e) => updateServiceArea(index, 'city', e.target.value)} />
                <Input placeholder="Description" value={area.description} onChange={(e) => updateServiceArea(index, 'description', e.target.value)} />
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeServiceArea(index)} className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={addServiceArea} className="w-full">
            <Plus className="w-4 h-4 mr-2" /> Add Service Area
          </Button>
        </div>
      </Section>

      {/* Testimonials */}
      <Section title="Testimonials" icon={Star}>
        <div className="space-y-4">
          {content.testimonials?.map((testimonial, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-center justify-between mb-3">
                <span className="font-semibold text-gray-700">Testimonial {index + 1}</span>
                <Button variant="ghost" size="sm" onClick={() => removeTestimonial(index)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mb-3">
                <Input placeholder="Customer Name" value={testimonial.name} onChange={(e) => updateTestimonial(index, 'name', e.target.value)} />
                <Input placeholder="Location (e.g., Houston, TX)" value={testimonial.location} onChange={(e) => updateTestimonial(index, 'location', e.target.value)} />
                <div>
                  <Select value={String(testimonial.rating)} onValueChange={(v) => updateTestimonial(index, 'rating', parseInt(v))}>
                    <SelectTrigger><SelectValue placeholder="Rating" /></SelectTrigger>
                    <SelectContent>
                      {[5, 4, 3, 2, 1].map(r => (
                        <SelectItem key={r} value={String(r)}>{r} Stars</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea 
                placeholder="Testimonial text..." 
                value={testimonial.text} 
                onChange={(e) => updateTestimonial(index, 'text', e.target.value)}
                rows={2}
              />
            </div>
          ))}
          <Button variant="outline" onClick={addTestimonial} className="w-full">
            <Plus className="w-4 h-4 mr-2" /> Add Testimonial
          </Button>
        </div>
      </Section>

      {/* Emergency Section */}
      <Section title="Emergency Section" icon={AlertTriangle}>
        <Field label="Headline" value={content.emergency_headline} onChange={(v) => updateField('emergency_headline', v)} />
        <Field label="Description" value={content.emergency_text} onChange={(v) => updateField('emergency_text', v)} multiline />
      </Section>

      {/* Mission Statement */}
      <Section title="Mission Statement" icon={Users}>
        <Field label="Mission Text" value={content.mission_text} onChange={(v) => updateField('mission_text', v)} multiline rows={4} />
      </Section>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Content?</DialogTitle>
            <DialogDescription>
              This will reset all content to the original defaults. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={resetContent}>Reset to Defaults</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
